/**
 * Family Members List Component
 *
 * Displays:
 * - List of family members
 * - Display name and email
 * - Admin badge
 * - Joined date
 * - Remove button (admin only, cannot remove self or other admins)
 *
 * Presentational component: No business logic, only displays data
 */
import React from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import type { TFunction } from '@dramadetector/shared';

interface FamilyMember {
  user_id: string;
  email: string;
  display_name: string | null;
  is_admin: boolean;
  joined_at: string;
}

interface FamilyMembersListProps {
  members: FamilyMember[];
  isFamilyAdmin: boolean;
  isRemoving: boolean;
  onRemoveMember: (userId: string, displayName: string) => void;
  locale: string;
  t: TFunction<"subscription">;
  tCommon: TFunction<"common">; // For common namespace keys (remove)
}

export function FamilyMembersList({
  members,
  isFamilyAdmin,
  isRemoving,
  onRemoveMember,
  locale,
  t,
  tCommon,
}: FamilyMembersListProps) {
  return (
    <View className="bg-white p-6 shadow-sm mb-4">
      <Text className="text-lg font-semibold text-gray-900 mb-3">
        {t('family.membersList')}
      </Text>
      {members.map((member) => (
        <View
          key={member.user_id}
          testID={`member-${member.email}`}
          className="border-b border-gray-100 py-4 last:border-b-0"
        >
          <View className="flex-row justify-between items-center">
            <View className="flex-1">
              <View className="flex-row items-center">
                <Text className="text-gray-900 font-medium">
                  {member.display_name || member.email}
                </Text>
                {member.is_admin && (
                  <View className="ml-2 bg-teal-100 px-2 py-1 rounded">
                    <Text className="text-teal-700 text-xs font-medium">
                      {t('family.admin')}
                    </Text>
                  </View>
                )}
              </View>
              {member.display_name && (
                <Text className="text-sm text-gray-500 mt-1">
                  {member.email}
                </Text>
              )}
              <Text className="text-xs text-gray-400 mt-1">
                {t('family.joinedAt', {
                  date: new Date(member.joined_at).toLocaleDateString(locale),
                })}
              </Text>
            </View>

            {/* Remove button (admin only, cannot remove self or other admins) */}
            {isFamilyAdmin && !member.is_admin && (
              <Pressable
                onPress={() =>
                  onRemoveMember(member.user_id, member.display_name || member.email)
                }
                disabled={isRemoving}
                className="ml-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg active:opacity-80"
              >
                {isRemoving ? (
                  <ActivityIndicator size="small" color="#EF4444" />
                ) : (
                  <Text className="text-red-600 text-sm font-medium">
                    {tCommon('remove')}
                  </Text>
                )}
              </Pressable>
            )}
          </View>
        </View>
      ))}
    </View>
  );
}
